import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Avatar from "@mui/material/Avatar";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Formik, FormikErrors } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";
import strings from "../../config/localization";
import { useChangePwdMutation } from "../../services/appln";
import { getMinPasswordLength } from "../constants";

interface FormData {
  oldPassword: string;
  newPassword: string;
}

export default function ChangePwd() {
  const defaultTheme = createTheme();

  const navigate = useNavigate();
  const [changePwd, params] = useChangePwdMutation();

  const initialValues: FormData = { oldPassword: "", newPassword: "" };

  if (params.isUninitialized || params.isError) {
    return (
      <Formik
        initialValues={initialValues}
        validate={(values) => {
          const errors: FormikErrors<FormData> = {};
          if (!values.oldPassword) {
            errors.oldPassword = strings.oldPasswordMandatory;
          }
          if (!values.newPassword) {
            errors.newPassword = strings.newPasswordMandatory;
          }
          if (values.newPassword.length < getMinPasswordLength()) {
            errors.newPassword = strings.formatString(
              strings.shortPassword,
              getMinPasswordLength(),
            ) as string;
          }

          if (values.newPassword === values.oldPassword) {
            errors.newPassword = strings.samePasswords;
          }
          return errors;
        }}
        onSubmit={(values, actions) => {
          console.log({ values, actions });
          actions.setSubmitting(false);
          changePwd(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <ThemeProvider theme={defaultTheme}>
            <Container component="main" maxWidth="xs">
              <CssBaseline />
              <Box
                sx={{
                  marginTop: 8,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                  <LockOutlinedIcon />
                </Avatar>
                <Typography component="h1" variant="h5">
                  {strings.changePwd}
                </Typography>
                {params.isError ? (
                  <div>
                    <Alert severity="error">
                      <AlertTitle>{strings.changePwdFailedTitle}</AlertTitle>
                      {strings.changePwdFailedMsg}
                    </Alert>
                  </div>
                ) : null}
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    required
                    margin="normal"
                    id="oldPassword"
                    name="oldPassword"
                    type="password"
                    label={strings.oldPassword}
                    value={values.oldPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.oldPassword && Boolean(errors.oldPassword)}
                    helperText={touched.oldPassword && errors.oldPassword}
                  />
                  <TextField
                    fullWidth
                    required
                    margin="normal"
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    label={strings.newPassword}
                    value={values.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.newPassword && Boolean(errors.newPassword)}
                    helperText={touched.newPassword && errors.newPassword}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    disabled={isSubmitting}
                    sx={{ mt: 3, mb: 2 }}
                  >
                    {strings.change}
                  </Button>
                </form>
              </Box>
            </Container>
          </ThemeProvider>
        )}
      </Formik>
    );
  }

  if (!params.isUninitialized && params.isLoading) {
    // application is being created. show progress indicator
    // backdrop is shown over empty screen...
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (!params.isUninitialized && params.isSuccess) {
    navigate("/");
  }

  return <div></div>;
}
